import {
  CircleCheck,
  CircleTimes,
  DownloadIcon,
  EditIcon,
  TrashIcon,
} from 'components/Icons';
import translation from 'locales/translation';
import { Fragment, useCallback, useReducer } from 'react';
import { useDispatch } from 'react-redux';

import {
  Button,
  ButtonThemes,
  Checkbox,
  Collapse,
  ControlLabel,
  IconButton,
  Input,
} from 'plataforma-fundacao-componentes';

import {
  assembleiaAssessorActionsDownloadDocument,
  assembleiaAssessorActionsRemoveDocument,
  assembleiasAssessorLoadEvento,
} from 'store/modules/AssembleiasAssessorActions';
import { IAssembleiasAssessorDocumentos } from 'store/modules/AssembleiasAssessorReducer';

import ErrorHandler from 'helpers/utils/ErrorHandler';

import { Col, Row } from 'plataforma-fundacao-componentes';

import moment from 'moment';
import styles from './styles.module.scss';

type DocType = {
  desc: string;
  flgDisponivelAssociado: boolean;
  isDownloading: boolean;
  isEditing: boolean;
  isRemoving: boolean;
};

interface Props extends IAssembleiasAssessorDocumentos {
  idEvento: number;
  dataCadastro: string | null | undefined;
  readAndAddOnly?: boolean;
  onFlagChange: (updatedDocument: IAssembleiasAssessorDocumentos) => void;
}

export default function Documento(props: Props) {
  const dispatch = useDispatch();

  const [documentInfo, setDocumentInfo] = useReducer(
    (prev: DocType, next: Partial<DocType>): DocType => {
      return { ...prev, ...next };
    },
    {
      isEditing: false,
      isDownloading: false,
      isRemoving: false,
      flgDisponivelAssociado: props.flgDisponivelAssociado,
      desc: props.desDocumento ?? '',
    }
  );

  const resetDesc = useCallback(() => {
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { idEvento, onFlagChange, ...updatedDocument } = props;
    updatedDocument.desDocumento = props.desDocumento;
    props.onFlagChange(updatedDocument);
    setDocumentInfo({
      ...documentInfo,
      desc: props.desDocumento ?? '',
      isEditing: false,
    });
  }, [documentInfo, props]);

  const downloadDocumento = useCallback(() => {
    setDocumentInfo({
      ...documentInfo,
      isDownloading: true,
    });
    dispatch(
      assembleiaAssessorActionsDownloadDocument(
        props.oidDocumento,
        props.nomDocumento,
        () =>
          setDocumentInfo({
            ...documentInfo,
            isDownloading: false,
          }),
        (error) => {
          ErrorHandler(error);
          setDocumentInfo({
            ...documentInfo,
            isDownloading: false,
          });
        }
      )
    );
  }, [documentInfo, dispatch, props.oidDocumento, props.nomDocumento]);

  const removeDocumento = useCallback(() => {
    setDocumentInfo({
      ...documentInfo,
      isRemoving: true,
    });
    dispatch(
      assembleiaAssessorActionsRemoveDocument(
        String(props.idEvento),
        props.oidDocumento,
        false,
        () => {
          dispatch(assembleiasAssessorLoadEvento(props.idEvento));
        },
        (error) => {
          setDocumentInfo({
            ...documentInfo,
            isRemoving: false,
          });
          ErrorHandler(error);
        }
      )
    );
  }, [documentInfo, dispatch, props.oidDocumento, props.idEvento]);

  const handleFlag = useCallback(
    (event: boolean) => {
      setDocumentInfo({
        ...documentInfo,
        flgDisponivelAssociado: event,
      });

      // eslint-disable-next-line unused-imports/no-unused-vars
      const { idEvento, onFlagChange, ...updatedDocument } = props;
      updatedDocument.flgDisponivelAssociado = event;
      props.onFlagChange(updatedDocument);
    },
    [documentInfo, props]
  );

  const handleChangeDesc = useCallback(() => {
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { idEvento, onFlagChange, ...updatedDocument } = props;
    updatedDocument.desDocumento = documentInfo.desc;
    props.onFlagChange(updatedDocument);
    setDocumentInfo({
      ...documentInfo,
      isEditing: false,
    });
  }, [documentInfo, props]);

  return (
    <Fragment>
      <Row className={styles.root_attachment} style={{ padding: '16px' }}>
        <Col cols={[11, 11, 11, 11, 11]}>
          <Row>
            <strong style={{ lineBreak: 'anywhere' }}>
              {props.nomDocumento}
            </strong>
            <Col noPadding style={{ paddingTop: '6px', opacity: '0.6' }}>
              <p>
                {moment(props.dataCadastro).format('DD/MM/yyyy - HH:mm:ss')}
              </p>
            </Col>
          </Row>
        </Col>
        <Col cols={[1, 1, 1, 1, 1]} buttonActionsCol end>
          <IconButton
            icon={<TrashIcon />}
            onClick={removeDocumento}
            disabled={
              props.readAndAddOnly ||
              documentInfo.isEditing ||
              documentInfo.isRemoving
            }
          />
        </Col>
        <Col>
          <Button
            theme={ButtonThemes.Secondary}
            rightIcon={<DownloadIcon />}
            onClick={downloadDocumento}
            loading={documentInfo.isDownloading}
            disabled={documentInfo.isDownloading}
            data-testid={'document-download'}
          >
            {translation.EditarDocumentosModal.buttonDownload}
          </Button>
        </Col>
        <Col cols={[11, 11, 11, 11, 11]}>
          <Input
            value={documentInfo.desc}
            placeholder={
              !documentInfo.desc?.length
                ? translation.EditarDocumentosModal.noDescription[
                    documentInfo.isEditing ? 'editing' : 'disabled'
                  ]
                : undefined
            }
            onChange={(e) => {
              setDocumentInfo({
                ...documentInfo,
                desc: e.target.value,
              });
            }}
            disabled={
              props.readAndAddOnly ||
              !documentInfo.isEditing ||
              documentInfo.isRemoving
            }
            maxLength={300}
            counter={documentInfo.isEditing}
            data-testid={'document-description'}
          />
        </Col>
        <Col cols={[1, 1, 1, 1, 1]} buttonActionsCol end>
          <IconButton
            onClick={() =>
              setDocumentInfo({
                ...documentInfo,
                isEditing: true,
              })
            }
            disabled={
              props.readAndAddOnly ||
              documentInfo.isEditing ||
              documentInfo.isRemoving
            }
            data-testid={'document-edit-button'}
            icon={<EditIcon />}
          />
        </Col>
        <Collapse
          opened={documentInfo.isEditing && !props.readAndAddOnly}
          animateOpacity
        >
          <Col buttonActionsCol end cols={[12, 11, 11, 11, 11]}>
            <Button
              theme={ButtonThemes.DangerDarkerOutline}
              leftIcon={<CircleTimes />}
              onClick={resetDesc}
              data-testid={'document-cancel-button'}
            >
              {translation.EditarDocumentosModal.buttonCancel}
            </Button>
            <Button
              theme={ButtonThemes.Primary}
              onClick={handleChangeDesc}
              leftIcon={<CircleCheck noFill />}
              data-testid={'document-change-button'}
            >
              {translation.EditarDocumentosModal.buttonSave}
            </Button>
          </Col>
        </Collapse>
        <Col noPaddingTop style={{ marginTop: '-8px' }}>
          <ControlLabel
            label={translation.EditarDocumentosModal.labelAvailable}
            control={
              <Checkbox
                disabled={
                  props.readAndAddOnly ||
                  documentInfo.isRemoving ||
                  documentInfo.isEditing
                }
                value={documentInfo.flgDisponivelAssociado}
                onChange={(e) => handleFlag(e)}
                data-testid={'document-visible'}
              />
            }
          />
        </Col>
      </Row>
    </Fragment>
  );
}
